import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { FilterMultiSelect } from '../../../components/FilterMultiSelect';
import { DateInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconBan,
  IconBell,
  IconCheck,
  IconDownload,
  IconEye,
  IconFile,
  IconFileText,
  IconPhoto,
  IconPlus,
  IconUpload,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { MedplumClient } from '@medplum/core';
import {
  AttachmentDisplay,
  AttachmentInput,
  useMedplum,
} from '@medplum/react';
import { getStatusColor, formatStatusText } from '../../../lib/statusUtils';
import type { Attachment, Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import { TEMPLATE_CATEGORY_SYSTEM } from '../../settings/template-library/types';
import { PreviewFormContent } from '../../settings/template-library/components/TemplatePreviewDrawer';
import { getPatientAccountRefs } from '../../../lib/clientFacingTasks';
import { executePortalTaskLifecycle, executeTaskMutation } from '../../../lib/taskLifecycleBots';
import { useCurrentUserPractitioner } from '../../../hooks/useCurrentUserPractitioner';
import { formatPractitionerPreferredLabel } from '@unify/fhir-model/resources/practitioner';
import { formatMedplumClientError } from '../../appointments/utils/medplumClientErrors';
import type { ClientDocument, CompletedForm, PendingFormTask } from '../data';
import { toEpisodeSelectOptions, type EpisodePickerItem } from '../data/episodeSelectOptions';
import { SearchInput } from '../../../components/SearchInput';
import { dateInputValueToDateOnly, parseDateOnlyLocal } from '../../../lib/dateHelpers';
import { FormResponseViewer, downloadFormAsPdf } from '../../../lib/FormResponseViewer';
import { downloadFhirAttachment } from '../../../lib/downloadFhirAttachment';
import { downloadDocumentAttachment } from '../../../lib/adminActionsService';
import { loadCompletedFormQuestionnaireResponse } from '../../../lib/resolveCompletedFormResponse';
import { documentReferenceMetaForPatient } from '../../../lib/documentClientVisibility';
import { notifyClientFormTask, notifyFormUploaded } from '../../../lib/notificationLifecycle';
import { isClientShareRestrictedTemplate, filterClientAssignableTemplates } from '../../../lib/clientShareRestrictedTemplates';
import { resolveRecipients } from '../../settings/rules-engine/services/recipientResolver';

interface FormsFilesTabProps {
  /** Patient ID for FHIR references */
  patientId: string;
  completedForms: CompletedForm[];
  documents: ClientDocument[];
  /** Active episodes for episode-scoped form requests */
  episodes?: EpisodePickerItem[];
  /** Pending form Tasks from the chart bundle (requested forms not yet completed). */
  pendingFormTasks?: PendingFormTask[];
  /** Called after a form request or upload creates backend resources */
  onRefetch?: () => void;
}

function formatDate(dateString: string): string {
  if (!dateString) return '--';
  try {
    return new Date(dateString).toLocaleDateString('en-US');
  } catch {
    return dateString;
  }
}

function getDocumentIcon(type: string) {
  if (type === 'PDF') return <IconFileText size={16} />;
  if (type === 'Image') return <IconPhoto size={16} />;
  return <IconFile size={16} />;
}

async function downloadClientDocumentAttachment(
  medplum: MedplumClient,
  attachment: Attachment,
  filename?: string,
  documentReferenceId?: string,
): Promise<void> {
  try {
    if (attachment.data || !documentReferenceId) {
      await downloadFhirAttachment(attachment, filename, medplum);
    } else {
      const downloaded = await downloadDocumentAttachment(medplum, documentReferenceId);
      await downloadFhirAttachment({
        data: downloaded.data,
        contentType: downloaded.contentType,
        title: downloaded.title,
      }, filename);
    }
  } catch (err) {
    notifications.show({
      color: 'red',
      title: 'Download failed',
      message: err instanceof Error ? err.message : 'Could not download this file.',
    });
  }
}

function blobFromBase64(data: string, contentType?: string): Blob {
  const binary = atob(data);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: contentType ?? 'application/octet-stream' });
}

function FilePreviewBody({ document }: { document: ClientDocument }): JSX.Element {
  const medplum = useMedplum();
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const kind =
    document.type === 'Image' || document.attachment?.contentType?.startsWith('image/')
      ? 'image'
      : document.type === 'PDF' || document.attachment?.contentType === 'application/pdf'
        ? 'pdf'
        : 'other';

  useEffect(() => {
    const attachment = document.attachment;
    if (!attachment?.data && !attachment?.url && !document.documentReferenceId) {
      setPreviewUrl(undefined);
      setLoading(false);
      setError('No attachment data available.');
      return;
    }

    let objectUrl: string | undefined;
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    void (async () => {
      try {
        if (attachment?.data && kind === 'image') {
          if (!cancelled) {
            setPreviewUrl(`data:${attachment.contentType ?? 'image/png'};base64,${attachment.data}`);
            setLoading(false);
          }
          return;
        }
        let blob: Blob;
        if (attachment?.data) {
          blob = blobFromBase64(attachment.data, attachment.contentType);
        } else if (document.documentReferenceId) {
          const downloaded = await downloadDocumentAttachment(medplum, document.documentReferenceId);
          blob = blobFromBase64(downloaded.data, downloaded.contentType ?? attachment?.contentType);
        } else {
          throw new Error('No attachment data available.');
        }
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setPreviewUrl(undefined);
          setLoading(false);
          setError(kind === 'other' ? 'Preview not available for this file type.' : 'Could not load file preview.');
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [document, kind, medplum]);

  if (loading) {
    return (
      <Stack align="center" justify="center" h={300}>
        <Loader size="sm" />
        <Text size="sm" c="gray.6">Loading preview…</Text>
      </Stack>
    );
  }

  if (previewUrl && kind === 'image') {
    return (
      <img
        src={previewUrl}
        alt={document.name}
        style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', display: 'block', margin: '0 auto' }}
      />
    );
  }

  if (previewUrl && kind === 'pdf') {
    return (
      <Box
        component="iframe"
        src={previewUrl}
        title={document.name}
        w="100%"
        h={480}
        style={{ border: 'none', background: 'white' }}
      />
    );
  }

  return (
    <Stack align="center" justify="center" h={300}>
      {getDocumentIcon(document.type)}
      <Text c="gray.6" ta="center" size="sm">
        {error ?? 'Preview not available for this file type.'}
      </Text>
      <Text size="xs" c="gray.5">{document.name}</Text>
    </Stack>
  );
}

/**
 * Forms & Files tab — provider view of pending/completed forms and uploaded files.
 */
export function FormsFilesTab({
  patientId,
  completedForms,
  documents,
  episodes,
  pendingFormTasks = [],
  onRefetch,
}: FormsFilesTabProps): JSX.Element {
  const medplum = useMedplum();
  const { practitioner } = useCurrentUserPractitioner();
  const [searchParams] = useSearchParams();
  const highlightTaskId = searchParams.get('taskId');

  // Upload state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadedAttachment, setUploadedAttachment] = useState<Attachment | undefined>(undefined);
  const [uploadCategory, setUploadCategory] = useState<string | null>(null);
  const [uploadDisplayName, setUploadDisplayName] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadEpisodeId, setUploadEpisodeId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Filter state
  const [formSearch, setFormSearch] = useState('');
  const [docSearch, setDocSearch] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState<string[]>(['PDF', 'Image', 'Document']);

  // Form template data
  const [formTemplates, setFormTemplates] = useState<Questionnaire[]>([]);

  // Modal state
  const [previewOpened, { open: openPreview, close: closePreview }] = useDisclosure(false);
  const [reminderOpened, { open: openReminder, close: closeReminder }] = useDisclosure(false);
  const [selectedDocument, setSelectedDocument] = useState<ClientDocument | null>(null);
  const [formModalOpened, setFormModalOpened] = useState(false);
  const [selectedForm, setSelectedForm] = useState<CompletedForm | null>(null);
  const [requestFormOpened, { open: openRequestForm, close: closeRequestForm }] = useDisclosure(false);
  const [requestFormType, setRequestFormType] = useState<string | null>(null);
  const [requestFormDueDate, setRequestFormDueDate] = useState<string | null>(null);
  const [requestFormMessage, setRequestFormMessage] = useState('');
  const [requestFormEpisodeId, setRequestFormEpisodeId] = useState<string | null>(null);
  const [requestFormSubmitting, setRequestFormSubmitting] = useState(false);
  const [patientAccountRefs, setPatientAccountRefs] = useState<Reference[]>([]);

  // Reminder state (tied to a specific pending task)
  const [reminderTask, setReminderTask] = useState<PendingFormTask | null>(null);
  const [reminderMessage, setReminderMessage] = useState('');
  const [reminderSubmitting, setReminderSubmitting] = useState(false);

  // Cancel confirm state
  const [cancelConfirmOpened, setCancelConfirmOpened] = useState(false);
  const [taskToCancel, setTaskToCancel] = useState<PendingFormTask | null>(null);
  const [cancelling, setCancelling] = useState(false);

  // Real QR loading for form viewer
  const [selectedFormQr, setSelectedFormQr] = useState<QuestionnaireResponse | null>(null);
  const [formQrLoading, setFormQrLoading] = useState(false);
  const [formQrLoadError, setFormQrLoadError] = useState<string | null>(null);

  const [pendingPreviewOpened, setPendingPreviewOpened] = useState(false);
  const [pendingPreviewTask, setPendingPreviewTask] = useState<PendingFormTask | null>(null);
  const [pendingPreviewQuestionnaire, setPendingPreviewQuestionnaire] = useState<Questionnaire | null>(null);
  const [pendingPreviewLoading, setPendingPreviewLoading] = useState(false);
  const [pendingPreviewError, setPendingPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!patientId) return;
    getPatientAccountRefs(medplum, patientId)
      .then(setPatientAccountRefs)
      .catch(() => setPatientAccountRefs([]));
  }, [medplum, patientId]);

  // Load active client-facing form templates only
  useEffect(() => {
    (async () => {
      try {
        const forms = await medplum.searchResources('Questionnaire', {
          _tag: `${TEMPLATE_CATEGORY_SYSTEM}|forms`,
          status: 'active',
        });
        setFormTemplates(filterClientAssignableTemplates(forms));
      } catch {
        setFormTemplates([]);
      }
    })();
  }, [medplum]);

  // Load QuestionnaireResponse when completed form modal opens
  useEffect(() => {
    if (!formModalOpened || !selectedForm) {
      setSelectedFormQr(null);
      setFormQrLoadError(null);
      return;
    }
    let cancelled = false;
    setFormQrLoading(true);
    setFormQrLoadError(null);
    void loadCompletedFormQuestionnaireResponse(medplum, selectedForm)
      .then((qr) => {
        if (cancelled) return;
        if (!qr) {
          setSelectedFormQr(null);
          setFormQrLoadError('Could not load form response.');
          return;
        }
        setSelectedFormQr(qr);
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedFormQr(null);
        setFormQrLoadError('Could not load form response.');
      })
      .finally(() => {
        if (!cancelled) setFormQrLoading(false);
      });
    return () => { cancelled = true; };
  }, [medplum, formModalOpened, selectedForm]);

  const openFormModal = (form: CompletedForm) => {
    setSelectedForm(form);
    setSelectedFormQr(null);
    setFormQrLoadError(null);
    setFormModalOpened(true);
  };

  const openPendingFormPreview = (task: PendingFormTask) => {
    setPendingPreviewTask(task);
    setPendingPreviewQuestionnaire(null);
    setPendingPreviewError(null);
    setPendingPreviewOpened(true);
    setPendingPreviewLoading(true);
    void (async () => {
      try {
        const fhirTask = await medplum.readResource('Task', task.taskId);
        const focusRef = fhirTask.focus?.reference;
        if (!focusRef?.startsWith('Questionnaire/')) {
          setPendingPreviewError('Could not load form template.');
          return;
        }
        const questionnaireId = focusRef.replace('Questionnaire/', '');
        const questionnaire = await medplum.readResource('Questionnaire', questionnaireId);
        setPendingPreviewQuestionnaire(questionnaire);
      } catch {
        setPendingPreviewError('Could not load form template.');
      } finally {
        setPendingPreviewLoading(false);
      }
    })();
  };

  const openReminderForTask = (task: PendingFormTask) => {
    setReminderTask(task);
    setReminderMessage('');
    openReminder();
  };

  const openCancelForTask = (task: PendingFormTask) => {
    setTaskToCancel(task);
    setCancelConfirmOpened(true);
  };

  const handleCancelTask = async () => {
    if (!taskToCancel) return;
    setCancelling(true);
    try {
      await executePortalTaskLifecycle(medplum, {
        action: 'cancel',
        taskId: taskToCancel.taskId,
      });
      setCancelConfirmOpened(false);
      setTaskToCancel(null);
      notifications.show({
        icon: <IconCheck size={18} />,
        title: 'Request cancelled',
        message: `${taskToCancel.name} has been cancelled.`,
        color: 'blue',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
      onRefetch?.();
    } catch (err) {
      notifications.show({ title: 'Failed to cancel', message: formatMedplumClientError(err), color: 'red' });
    } finally {
      setCancelling(false);
    }
  };

  const handleSendReminder = async () => {
    if (!reminderTask) return;
    setReminderSubmitting(true);
    try {
      const task = await medplum.readResource('Task', reminderTask.taskId);
      const noteText = [
        `Reminder sent on ${new Date().toLocaleString()}`,
        reminderMessage.trim() ? `Message: ${reminderMessage.trim()}` : null,
      ].filter(Boolean).join(' — ');
      const notificationBody =
        reminderMessage.trim() ||
        `Please complete ${reminderTask.name}. Your care team is waiting for this form.`;

      await executeTaskMutation(medplum, {
        action: 'update-payload',
        task: {
          ...task,
          note: [...(task.note ?? []), { text: noteText, time: new Date().toISOString() }],
        },
      });
      const notificationResult = await notifyClientFormTask(medplum, {
        patientId,
        taskId: reminderTask.taskId,
        formName: reminderTask.name,
        title: `Reminder: ${reminderTask.name}`,
        body: notificationBody,
        accountRefs: patientAccountRefs,
      });

      closeReminder();
      setReminderMessage('');
      setReminderTask(null);
      notifications.show({
        icon: <IconCheck size={18} />,
        title: notificationResult.inAppCreated ? 'Reminder sent' : 'Reminder recorded',
        message: notificationResult.inAppCreated
          ? 'The client has been notified in their portal.'
          : 'A reminder note was added, but no in-app notification was created (preferences or duplicate).',
        color: notificationResult.inAppCreated ? 'green' : 'yellow',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
    } catch (err) {
      notifications.show({ title: 'Failed to send reminder', message: formatMedplumClientError(err), color: 'red' });
    } finally {
      setReminderSubmitting(false);
    }
  };

  type UnifiedFormRow =
    | { kind: 'pending'; data: PendingFormTask }
    | { kind: 'completed'; data: CompletedForm };

  const completedTaskIds = useMemo(() => {
    const set = new Set<string>();
    for (const f of completedForms) {
      if (f.sourceTaskId) set.add(f.sourceTaskId);
    }
    return set;
  }, [completedForms]);

  const unifiedFormRows = useMemo((): UnifiedFormRow[] => {
    const pendingRows: UnifiedFormRow[] = pendingFormTasks
      .filter((t) => t.status !== 'cancelled' as string && !completedTaskIds.has(t.taskId))
      .map((t) => ({ kind: 'pending' as const, data: t }));
    const completedRows: UnifiedFormRow[] = completedForms.map((f) => ({ kind: 'completed' as const, data: f }));
    return [...pendingRows, ...completedRows];
  }, [pendingFormTasks, completedForms, completedTaskIds]);

  const filteredForms = useMemo(() => {
    if (!formSearch) return unifiedFormRows;
    const query = formSearch.toLowerCase();
    return unifiedFormRows.filter((row) => {
      const name = row.kind === 'pending' ? row.data.name : row.data.name;
      return name.toLowerCase().includes(query);
    });
  }, [unifiedFormRows, formSearch]);

  const filteredDocuments = useMemo(() => {
    return documents.filter((doc) => {
      if (docTypeFilter.length === 0) return false;
      if (!docTypeFilter.includes(doc.type)) return false;
      if (docSearch) {
        const query = docSearch.toLowerCase();
        return (
          doc.name.toLowerCase().includes(query) ||
          doc.uploadedBy.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [documents, docTypeFilter, docSearch]);

  const handlePreviewDocument = (doc: ClientDocument) => {
    setSelectedDocument(doc);
    openPreview();
  };

  const handleRequestFormSubmit = async () => {
    if (!requestFormType) {
      notifications.show({
        icon: <IconAlertTriangle size={18} />,
        title: 'Form required',
        message: 'Please select a form to request.',
        color: 'yellow',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
      return;
    }

    const selectedTemplate = formTemplates.find((q) => q.id === requestFormType);
    if (selectedTemplate && isClientShareRestrictedTemplate(selectedTemplate)) {
      notifications.show({
        icon: <IconAlertTriangle size={18} />,
        title: 'Cannot request form',
        message: 'This form cannot be assigned to the client.',
        color: 'yellow',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
      return;
    }

    const formLabel = selectedTemplate?.title ?? selectedTemplate?.name ?? requestFormType;

    setRequestFormSubmitting(true);
    try {
      if (selectedTemplate?.id) {
        const created = await executePortalTaskLifecycle(medplum, {
          action: 'create-form-assignment',
          patientId,
          questionnaireId: selectedTemplate.id,
          description: formLabel,
          due: requestFormDueDate ?? undefined,
          episodeId: requestFormEpisodeId ?? undefined,
        });

        const notifyResult = await notifyClientFormTask(medplum, {
          patientId,
          taskId: created.id!,
          formName: formLabel,
          title: `New form: ${formLabel}`,
          body: requestFormMessage.trim() || `Please complete ${formLabel}.`,
          accountRefs: patientAccountRefs,
        });

        if (!notifyResult.inAppCreated) {
          console.warn('[FormsFilesTab] Form assigned but in-app notification was not created');
        }
      }

      closeRequestFormModal();
      notifications.show({
        icon: <IconCheck size={18} />,
        title: 'Form request sent',
        message: `${formLabel}. The client will be notified to complete it.`,
        color: 'green',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
      onRefetch?.();
    } catch (err) {
      notifications.show({
        title: 'Failed to request form',
        message: formatMedplumClientError(err),
        color: 'red',
      });
    } finally {
      setRequestFormSubmitting(false);
    }
  };

  const closeRequestFormModal = () => {
    closeRequestForm();
    setRequestFormType(null);
    setRequestFormDueDate(null);
    setRequestFormMessage('');
    setRequestFormEpisodeId(null);
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setUploadedAttachment(undefined);
    setUploadCategory(null);
    setUploadDisplayName('');
    setUploadNotes('');
    setUploadEpisodeId(null);
  };

  const handleUploadSubmit = async () => {
    if (!uploadedAttachment) return;
    setUploading(true);
    try {
      const attachment: Attachment = {
        ...uploadedAttachment,
        ...(uploadDisplayName.trim() ? { title: uploadDisplayName.trim() } : {}),
      };

      const episodeRelated = uploadEpisodeId
        ? [{ reference: `EpisodeOfCare/${uploadEpisodeId}` }]
        : undefined;

      const uploadMeta = documentReferenceMetaForPatient({
        patientId,
        accountRefs: patientAccountRefs,
      });

      const created = await medplum.createResource({
        resourceType: 'DocumentReference',
        status: 'current',
        subject: { reference: `Patient/${patientId}` },
        ...(practitioner?.id
          ? { author: [{ reference: `Practitioner/${practitioner.id}`, display: formatPractitionerPreferredLabel(practitioner) || undefined }] }
          : {}),
        date: new Date().toISOString(),
        content: [{ attachment }],
        ...(uploadNotes.trim() ? { description: uploadNotes.trim() } : {}),
        ...(uploadCategory
          ? {
              category: [{
                coding: [{
                  system: 'https://unify.health/fhir/document-category',
                  code: uploadCategory,
                  display: uploadCategory.charAt(0).toUpperCase() + uploadCategory.slice(1),
                }],
              }],
            }
          : {}),
        ...(episodeRelated ? { context: { related: episodeRelated } } : {}),
        meta: uploadMeta,
      });

      if (created.id) {
        const careTeam = await resolveRecipients(medplum, ['care-team-primary-contact'], {
          patientId,
          triggerResourceType: 'DocumentReference',
          triggerResourceId: created.id,
        });
        const primary = careTeam.find((r) => r.resolved && r.resourceReference);
        if (primary?.resourceReference) {
          await notifyFormUploaded(medplum, {
            recipientRef: primary.resourceReference,
            patientId,
            documentReferenceId: created.id,
            documentName: uploadDisplayName.trim() || attachment.title || 'Document',
            accountRefs: patientAccountRefs,
          }).catch(() => undefined);
        }
      }

      closeUploadModal();
      notifications.show({
        icon: <IconCheck size={18} />,
        title: 'File uploaded',
        message: 'Document has been saved to the client\'s chart.',
        color: 'green',
        withCloseButton: true,
        withBorder: true,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
      onRefetch?.();
    } catch (err) {
      notifications.show({
        title: 'Upload failed',
        message: formatMedplumClientError(err),
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Stack gap="lg">
      {/* Forms */}
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Title order={4}>Forms</Title>
          <Group gap="md">
            <SearchInput
              placeholder="Search forms..."
              value={formSearch}
              onChange={(e) => setFormSearch(e.currentTarget.value)}
              w={280}
            />
            <Button leftSection={<IconPlus size={16} />} onClick={openRequestForm}>
              Request form
            </Button>
          </Group>
        </Group>

        {filteredForms.length === 0 ? (
          <Text c="gray.6" ta="center" py="xl">
            {formSearch
              ? 'No forms match your search'
              : formTemplates.length === 0
                ? 'No form templates available. Add forms in Settings → Documentation → Template Library.'
                : 'No forms yet.'}
          </Text>
        ) : (
          <ScrollArea.Autosize mah={400} type="scroll" offsetScrollbars>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Form</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredForms.map((row) => {
                  if (row.kind === 'pending') {
                    const pt = row.data;
                    const statusColor = pt.status === 'overdue' ? 'red' : pt.status === 'in-progress' ? 'blue' : 'yellow';
                    const statusLabel = pt.status === 'overdue' ? 'Overdue' : pt.status === 'in-progress' ? 'In Progress' : 'Requested';
                    const isHighlighted = highlightTaskId === pt.taskId;
                    return (
                      <Table.Tr
                        key={pt.id}
                        style={{
                          cursor: 'pointer',
                          ...(isHighlighted ? { backgroundColor: 'var(--mantine-color-blue-0)' } : {}),
                        }}
                        onClick={() => openPendingFormPreview(pt)}
                      >
                        <Table.Td>
                          <Group gap="xs">
                            <IconFileText size={18} color="gray" />
                            <Stack gap={0}>
                              <Text size="sm">{pt.name}</Text>
                              {pt.episodeLabel && <Text size="xs" c="gray.5">{pt.episodeLabel}</Text>}
                            </Stack>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" color={statusColor} size="sm">
                            {statusLabel}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap={0}>
                            {pt.requestedDate && <Text size="xs" c="gray.6">Requested {formatDate(pt.requestedDate)}</Text>}
                            {pt.dueDate && <Text size="xs" c="gray.6">Due {formatDate(pt.dueDate)}</Text>}
                          </Stack>
                        </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Menu position="bottom-end" shadow="sm">
                            <Menu.Target>
                              <ActionIcon variant="subtle" color="gray" size="sm">
                                <IconBell size={14} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconBell size={14} />}
                                onClick={() => openReminderForTask(pt)}
                              >
                                Send Reminder
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconBan size={14} />}
                                color="red"
                                onClick={() => openCancelForTask(pt)}
                              >
                                Cancel Request
                              </Menu.Item>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    );
                  }

                  const form = row.data;
                  const isHighlighted = highlightTaskId === form.sourceTaskId;
                  return (
                    <Table.Tr
                      key={form.id}
                      style={{
                        cursor: 'pointer',
                        ...(isHighlighted ? { backgroundColor: 'var(--mantine-color-blue-0)' } : {}),
                      }}
                      onClick={() => openFormModal(form)}
                    >
                      <Table.Td>
                        <Group gap="xs">
                          <IconFileText size={18} color="gray" />
                          <Text size="sm">{form.name}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge variant="light" color="green" size="sm">
                          Completed
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="gray.6">{formatDate(form.completedDate)}</Text>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => openFormModal(form)}>
                          <IconEye size={14} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}

        <Text size="sm" c="gray.6" mt="md">
          Showing {filteredForms.length} of {unifiedFormRows.length} forms
        </Text>
      </Card>

      {/* Files */}
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Title order={4}>Files</Title>
          <Group gap="md">
            <SearchInput
              placeholder="Search files..."
              value={docSearch}
              onChange={(e) => setDocSearch(e.currentTarget.value)}
              w={280}
            />
            <FilterMultiSelect
              allLabel="All Types"
              data={[
                { value: 'PDF', label: 'PDF' },
                { value: 'Image', label: 'Image' },
                { value: 'Document', label: 'File' },
              ]}
              value={docTypeFilter}
              onChange={setDocTypeFilter}
              w={240}
            />
            <Button leftSection={<IconUpload size={16} />} onClick={() => setUploadModalOpen(true)}>
              Upload File
            </Button>
          </Group>
        </Group>

        {filteredDocuments.length === 0 ? (
          <Text c="gray.6" ta="center" py="xl">
            {docSearch || docTypeFilter.length > 0
              ? 'No files match your filters'
              : 'No files uploaded. Use "Upload File" to add documents.'}
          </Text>
        ) : (
          <ScrollArea.Autosize mah={400} type="scroll" offsetScrollbars>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Category</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Size</Table.Th>
                  <Table.Th>Uploaded By</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredDocuments.map((doc) => (
                  <Table.Tr key={doc.id} style={{ cursor: 'pointer' }} onClick={() => handlePreviewDocument(doc)}>
                    <Table.Td>
                      <Group gap="xs">
                        {getDocumentIcon(doc.type)}
                        <Text size="sm">{doc.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="gray.6">{doc.category ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" size="sm">{doc.type}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="gray.6">{doc.size}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{doc.uploadedBy}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(doc.uploadedDate)}</Text>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="4" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          title="Preview"
                          onClick={() => handlePreviewDocument(doc)}
                        >
                          <IconEye size={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          title="Download"
                          onClick={() => {
                            if (doc.attachment) {
                              void downloadClientDocumentAttachment(
                                medplum,
                                doc.attachment,
                                doc.name,
                                doc.documentReferenceId,
                              );
                            } else {
                              notifications.show({ color: 'yellow', title: 'Download unavailable', message: 'No attachment data available.' });
                            }
                          }}
                        >
                          <IconDownload size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        )}

        <Text size="sm" c="gray.6" mt="md">
          Showing {filteredDocuments.length} of {documents.length} files
        </Text>
      </Card>

      {/* Form View Modal */}
      <Modal
        opened={formModalOpened}
        onClose={() => {
          setFormModalOpened(false);
          setSelectedForm(null);
          setSelectedFormQr(null);
          setFormQrLoadError(null);
        }}
        title={selectedForm?.name ?? 'Form'}
        size="xl"
        centered
        styles={{
          body: { paddingTop: 0, paddingBottom: 'var(--mantine-spacing-lg)' },
          content: { maxHeight: '92vh', display: 'flex', flexDirection: 'column' },
          header: { marginBottom: 0, paddingBottom: 'var(--mantine-spacing-sm)' },
        }}
      >
        {selectedForm && (
          <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {/* Metadata row */}
            <Group justify="space-between" align="center" style={{ flexShrink: 0 }}>
              <Group gap="xs">
                <Badge variant="light" size="sm" color="green">
                  Completed {formatDate(selectedForm.completedDate)}
                </Badge>
                {selectedForm.episodeId && (
                  <Badge variant="outline" size="sm" color="gray">
                    {episodes?.find((e) => e.id === selectedForm.episodeId)?.name ?? 'Episode'}
                  </Badge>
                )}
              </Group>
            </Group>

            <Divider style={{ flexShrink: 0 }} />

            {/* Response body */}
            <Card
              withBorder
              p="lg"
              bg="gray.0"
              style={{
                borderColor: 'var(--mantine-color-gray-3)',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ScrollArea.Autosize mah={560} offsetScrollbars type="scroll" style={{ flex: 1 }}>
                <Box pb="lg" pr="xs">
                  {formQrLoading ? (
                    <Group justify="center" py="xl"><Loader size="sm" /></Group>
                  ) : formQrLoadError ? (
                    <Text c="gray.6" ta="center" py="xl">{formQrLoadError}</Text>
                  ) : selectedFormQr ? (
                    <FormResponseViewer questionnaireResponse={selectedFormQr} />
                  ) : (
                    <Text c="gray.6" ta="center" py="xl">No form response available.</Text>
                  )}
                </Box>
              </ScrollArea.Autosize>
            </Card>

            <Divider style={{ flexShrink: 0 }} />

            {/* Footer actions */}
            <Group justify="space-between" style={{ flexShrink: 0 }}>
              <Button
                variant="default"
                onClick={() => {
                  setFormModalOpened(false);
                  setSelectedForm(null);
                  setSelectedFormQr(null);
                }}
              >
                Close
              </Button>
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                disabled={!selectedFormQr}
                  onClick={() => {
                    if (!selectedFormQr) return;
                    void downloadFormAsPdf(selectedForm.name, selectedFormQr);
                  }}
              >
                Download PDF
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Pending form template preview */}
      <Modal
        opened={pendingPreviewOpened}
        onClose={() => {
          setPendingPreviewOpened(false);
          setPendingPreviewTask(null);
          setPendingPreviewQuestionnaire(null);
          setPendingPreviewError(null);
        }}
        title={pendingPreviewTask?.name ?? 'Form'}
        size="xl"
        centered
        styles={{
          body: { paddingTop: 0, paddingBottom: 'var(--mantine-spacing-lg)' },
          content: { maxHeight: '92vh', display: 'flex', flexDirection: 'column' },
          header: { marginBottom: 0, paddingBottom: 'var(--mantine-spacing-sm)' },
        }}
      >
        {pendingPreviewTask && (
          <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Badge variant="light" color="yellow" w="fit-content" style={{ flexShrink: 0 }}>
              Awaiting client completion
            </Badge>
            {pendingPreviewLoading ? (
              <Group justify="center" py="xl"><Loader size="sm" /></Group>
            ) : pendingPreviewError ? (
              <Text c="gray.6" ta="center" py="xl">{pendingPreviewError}</Text>
            ) : pendingPreviewQuestionnaire ? (
              <Card
                p="lg"
                bg="gray.0"
                style={{
                  borderColor: 'var(--mantine-color-gray-3)',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <ScrollArea.Autosize mah={560} offsetScrollbars type="scroll" style={{ flex: 1 }}>
                  <Box pb="lg" pr="xs">
                    <PreviewFormContent questionnaire={pendingPreviewQuestionnaire} readOnly />
                  </Box>
                </ScrollArea.Autosize>
              </Card>
            ) : null}
            <Group justify="flex-end" style={{ flexShrink: 0 }}>
              <Button variant="default" onClick={() => setPendingPreviewOpened(false)}>
                Close
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* File Preview Modal */}
      <Modal
        opened={previewOpened}
        onClose={closePreview}
        title="File Preview"
        size="lg"
        centered
      >
        {selectedDocument && (
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="xs">
                {getDocumentIcon(selectedDocument.type)}
                <Title order={4}>{selectedDocument.name}</Title>
              </Group>
              <Badge color={getStatusColor(selectedDocument.status)} variant="light">
                {formatStatusText(selectedDocument.status)}
              </Badge>
            </Group>

            <Divider />

            <Card withBorder bg="gray.1" style={{ minHeight: 300 }}>
              <FilePreviewBody document={selectedDocument} />
            </Card>

            <Group gap="md">
              <Text size="sm" c="gray.6">Size: {selectedDocument.size}</Text>
              <Text size="sm" c="gray.6">•</Text>
              <Text size="sm" c="gray.6">Uploaded by {selectedDocument.uploadedBy}</Text>
              <Text size="sm" c="gray.6">•</Text>
              <Text size="sm" c="gray.6">{formatDate(selectedDocument.uploadedDate)}</Text>
            </Group>

            <Divider />

            <Group justify="space-between">
              <Button variant="default" onClick={closePreview}>
                Close
              </Button>
              <Button
                leftSection={<IconDownload size={16} />}
                onClick={() => {
                  if (selectedDocument.attachment) {
                    void downloadClientDocumentAttachment(
                      medplum,
                      selectedDocument.attachment,
                      selectedDocument.name,
                      selectedDocument.documentReferenceId,
                    );
                  } else {
                    notifications.show({ color: 'yellow', title: 'Download unavailable', message: 'No attachment data available.' });
                  }
                }}
              >
                Download
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Send Reminder Modal (tied to specific task) */}
      <Modal
        opened={reminderOpened}
        onClose={() => { closeReminder(); setReminderTask(null); setReminderMessage(''); }}
        title="Send Form Reminder"
        size="md"
        centered
      >
        <Stack gap="md">
          {reminderTask && (
            <Text size="sm">
              Send a reminder for{' '}
              <Text span fw={500}>{reminderTask.name}</Text>.
            </Text>
          )}
          <Text size="sm" c="gray.6">
            The client will receive an in-app notification in their portal. A reminder note is also added to the form request.
          </Text>

          <Textarea
            label="Note (optional)"
            placeholder="Add context or a personalized message..."
            value={reminderMessage}
            onChange={(e) => setReminderMessage(e.currentTarget.value)}
            minRows={3}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => { closeReminder(); setReminderTask(null); setReminderMessage(''); }}>
              Cancel
            </Button>
            <Button
              leftSection={<IconBell size={16} />}
              onClick={handleSendReminder}
              loading={reminderSubmitting}
            >
              Send Reminder
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Cancel Request Confirm Modal */}
      <Modal
        opened={cancelConfirmOpened}
        onClose={() => { setCancelConfirmOpened(false); setTaskToCancel(null); }}
        title="Cancel Form Request"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Cancel the request for{' '}
            <Text span fw={500}>{taskToCancel?.name}</Text>?
            The client will no longer see this in their pending forms.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => { setCancelConfirmOpened(false); setTaskToCancel(null); }}>
              Keep
            </Button>
            <Button color="red" leftSection={<IconBan size={16} />} onClick={handleCancelTask} loading={cancelling}>
              Cancel Request
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Request Form Modal */}
      <Modal
        opened={requestFormOpened}
        onClose={closeRequestFormModal}
        title="Request Form"
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="gray.6">
            Request that the client complete a form. It will appear in their portal under pending forms.
          </Text>

          {formTemplates.length === 0 ? (
            <Text size="sm" c="yellow.7" ta="center" py="sm">
              No active form templates found. Add forms in Settings → Documentation → Template Library.
            </Text>
          ) : (
            <Select
              label="Form to request"
              placeholder="Choose a form..."
              value={requestFormType}
              onChange={setRequestFormType}
              data={formTemplates.map((q) => ({
                value: q.id!,
                label: q.title ?? q.name ?? q.id!,
              }))}
              searchable
              required
            />
          )}

          {episodes && episodes.length > 0 && (
            <Select
              label="Episode of Care (optional)"
              placeholder="Select episode..."
              data={toEpisodeSelectOptions(episodes)}
              value={requestFormEpisodeId}
              onChange={setRequestFormEpisodeId}
              clearable
            />
          )}

          <DateInput
            label="Due date (optional)"
            placeholder="Pick a date"
            value={requestFormDueDate ? parseDateOnlyLocal(requestFormDueDate) : null}
            onChange={(v) => setRequestFormDueDate(v ? dateInputValueToDateOnly(v) : null)}
            clearable
          />

          <Textarea
            label="Message to client (optional)"
            placeholder="Add a note for the client..."
            value={requestFormMessage}
            onChange={(e) => setRequestFormMessage(e.currentTarget.value)}
            minRows={3}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="default" onClick={closeRequestFormModal}>
              Cancel
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleRequestFormSubmit}
              loading={requestFormSubmitting}
              disabled={formTemplates.length === 0}
            >
              Request Form
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Upload File Modal */}
      <Modal
        opened={uploadModalOpen}
        onClose={closeUploadModal}
        title="Upload File"
        size="md"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="gray.6">
            Upload a document to the client&apos;s chart. Supported formats include PDF, images, and common file types.
          </Text>

          <div>
            <Text size="sm" fw={500} mb={4}>
              Select File <Text span c="red">*</Text>
            </Text>
            <AttachmentInput
              path="DocumentReference.content.attachment"
              name="document-upload"
              onChange={(attachment) => {
                setUploadedAttachment(attachment);
                if (attachment?.title && !uploadDisplayName) {
                  setUploadDisplayName(attachment.title);
                }
              }}
            />
          </div>

          {uploadedAttachment && (
            <Card withBorder bg="green.0">
              <Group gap="xs">
                <IconFile size={16} color="green" />
                <Text size="sm" c="green">
                  File ready: <AttachmentDisplay value={uploadedAttachment} />
                </Text>
              </Group>
            </Card>
          )}

          <TextInput
            label="Display name"
            placeholder="Leave blank to use file name"
            value={uploadDisplayName}
            onChange={(e) => setUploadDisplayName(e.currentTarget.value)}
          />

          <Select
            label="Document Category"
            placeholder="Select category..."
            data={[
              { value: 'insurance', label: 'Insurance Document' },
              { value: 'id', label: 'Identification' },
              { value: 'clinical', label: 'Clinical Document' },
              { value: 'consent', label: 'Consent Form' },
              { value: 'other', label: 'Other' },
            ]}
            value={uploadCategory}
            onChange={setUploadCategory}
          />

          {episodes && episodes.length > 0 && (
            <Select
              label="Episode of Care (optional)"
              placeholder="Associate with an episode..."
              data={toEpisodeSelectOptions(episodes)}
              value={uploadEpisodeId}
              onChange={setUploadEpisodeId}
              clearable
            />
          )}

          <Textarea
            label="Notes (optional)"
            placeholder="Add any notes about this document..."
            value={uploadNotes}
            onChange={(e) => setUploadNotes(e.currentTarget.value)}
            minRows={2}
          />

          <Divider />

          <Group justify="flex-end">
            <Button variant="default" onClick={closeUploadModal}>
              Cancel
            </Button>
            <Button
              leftSection={<IconUpload size={16} />}
              disabled={!uploadedAttachment}
              loading={uploading}
              onClick={handleUploadSubmit}
            >
              Upload
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
